import { create } from 'zustand';
import {
  getAudiences,
  getNotificationHistory,
  createCampaign,
  sendCampaign,
  Audience,
  NotificationRecord,
  CampaignData,
} from '@/services/PagesServices/MarketingAndNotificationService';

interface MarketingAndNotificationState {
  audiences: Audience[] | null;
  notificationHistory: NotificationRecord[] | null;
  loading: boolean;
  sending: boolean;
  error: string | null;

  fetchAudiences: () => Promise<void>;
  fetchNotificationHistory: () => Promise<void>;
  createAndSendCampaign: (data: CampaignData, sendImmediately: boolean) => Promise<{ success: boolean; error?: string }>;
  resetError: () => void;
}

const useMarketingAndNotificationStore = create<MarketingAndNotificationState>((set, get) => ({
  audiences: null,
  notificationHistory: null,
  loading: false,
  sending: false,
  error: null,

  fetchAudiences: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAudiences();
      set({ audiences: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchNotificationHistory: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getNotificationHistory();
      set({ notificationHistory: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createAndSendCampaign: async (data: CampaignData, sendImmediately: boolean) => {
    try {
      set({ sending: true, error: null });

      // First create the campaign
      const createResponse = await createCampaign(data);

      if (!createResponse.success) {
        set({ sending: false, error: createResponse.error?.message || 'Failed to create campaign' });
        return { success: false, error: createResponse.error?.message };
      }

      // If sendImmediately is true and campaign was created, send it
      if (sendImmediately && createResponse.data?.uuid) {
        const sendResponse = await sendCampaign(createResponse.data.uuid);

        if (!sendResponse.success) {
          set({ sending: false, error: sendResponse.error?.message || 'Campaign created but failed to send' });
          return { success: false, error: sendResponse.error?.message };
        }
      }

      set({ sending: false });

      // Refresh history after successful send
      get().fetchNotificationHistory();

      return { success: true };
    } catch (error: any) {
      set({ sending: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  resetError: () => {
    set({ error: null });
  },
}));

export default useMarketingAndNotificationStore;
